// Signing in — or creating the account — with the phone number an invitation was sent to (ADR-0046,
// spec/07 §3.1, §6.5), on the accept page itself. The number is the invitation's, shown and not to be
// changed: a code goes to it (`/v1/auth/phone-number/send-otp`), and the code signs the person in
// (`/v1/auth/phone-number/verify`). A number with no account yet is answered `CONSENT_REQUIRED` and keeps its
// code: the terms and privacy are asked once, and the same code, sent again with them, creates the account —
// the way the phone sign-up has always created one. Nothing else is asked for.

import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, TextField } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../api/client';
import { fetchBrandLegal } from '../api/meta';
import { PROBLEM, problemCode } from '../api/problems';
import { queryKeys } from '../api/queries';
import { useAuthFailure } from './auth-errors';
import { ConsentLabel } from './consent';
import { Failure, TextInput } from './form-controls';

type Step = 'send' | 'code';

export function InvitedPhoneSignIn({ phoneNumber, onSignedIn }: { readonly phoneNumber: string; readonly onSignedIn: () => Promise<void> }): ReactNode {
  const { t } = useTranslation('console');
  const failureText = useAuthFailure();
  const api = useApi();
  const [step, setStep] = useState<Step>('send');
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  // A number with no account yet: the consent its account is created with, asked only then.
  const [needsConsent, setNeedsConsent] = useState(false);
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const legal = useQuery({ queryKey: queryKeys.meta, queryFn: () => fetchBrandLegal(api), staleTime: 300_000, enabled: needsConsent });
  const form = useForm<{ code: string }>({ defaultValues: { code: '' } });

  const send = async () => {
    setFailure(null);
    setSending(true);
    try {
      await api.sendPhoneNumberOTP({ body: { phoneNumber } });
      setStep('code');
    } catch (error) {
      setFailure(failureText(error));
    } finally {
      setSending(false);
    }
  };

  const verify = form.handleSubmit(async ({ code }) => {
    setFailure(null);
    if (needsConsent && !consent) {
      setFailure(t('signUp.consent.required'));
      return;
    }
    const agreed =
      needsConsent && legal.data !== undefined
        ? { terms: legal.data.terms.version, privacy: legal.data.privacy.version, ...(marketing ? { marketing: true } : {}) }
        : undefined;
    try {
      await api.verifyPhoneNumber({ body: { phoneNumber, code: code.trim(), ...(agreed === undefined ? {} : { consent: agreed }) } });
      await onSignedIn();
    } catch (error) {
      if (problemCode(error) === 'CONSENT_REQUIRED' || problemCode(error) === PROBLEM.consentRequired) {
        // A new number: the code is kept for the retry, with the terms agreed.
        setNeedsConsent(true);
        setFailure(null);
        return;
      }
      setFailure(failureText(error));
    }
  });

  return (
    <div className="console-form">
      <TextField label={t('signIn.phone.number')} description={t('invitations.accept.phoneLocked')} type="tel" name="phoneNumber" value={phoneNumber} readOnly />
      <Failure message={failure} />
      {step === 'send' ? (
        <Button variant="primary" busy={sending} onClick={() => void send()}>
          {t('signIn.phone.send')}
        </Button>
      ) : (
        <form className="console-form" onSubmit={(event) => void verify(event)} noValidate>
          <p role="status">{t('signIn.phone.sent', { phone: phoneNumber })}</p>
          <TextInput
            control={form.control}
            name="code"
            label={t('signIn.phone.code')}
            description={t('signIn.phone.codeHint', { phone: phoneNumber })}
            inputMode="numeric"
            autoComplete="one-time-code"
            numeric
            rules={{ required: t('signIn.required') }}
          />
          {needsConsent ? (
            <>
              <p className="console-muted">{t('invitations.accept.newNumber')}</p>
              {legal.data === undefined && !legal.isPending ? (
                <p className="console-alert" role="status">
                  {t('signUp.legal.unavailable')}
                </p>
              ) : null}
              <Checkbox
                label={<ConsentLabel legal={legal.data} />}
                checked={consent}
                onCheckedChange={(checked) => setConsent(checked === true)}
                disabled={legal.data === undefined}
              />
              <Checkbox
                label={t('signUp.consent.marketing')}
                checked={marketing}
                onCheckedChange={(checked) => setMarketing(checked === true)}
                disabled={legal.data === undefined}
              />
            </>
          ) : null}
          <div className="console-form__actions">
            <Button type="submit" variant="primary" busy={form.formState.isSubmitting} disabled={needsConsent && legal.data === undefined}>
              {needsConsent ? t('invitations.accept.createWithPhone') : t('signIn.phone.verify')}
            </Button>
            <Button variant="ghost" busy={sending} onClick={() => void send()}>
              {t('invitations.accept.sendAgain')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
